"use client";

import * as React from "react";
import { UserButton } from "@clerk/nextjs";
import { OrgSwitcher } from "@/components/org-switcher";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  LayoutDashboard,
  Users,
  Truck,
  Package,
  AlertTriangle,
  DollarSign,
  Settings,
  Clock,
  MapPinOff,
  UserX,
  Workflow,
  GitBranch,
  PowerOff,
  KeyRound,
  Inbox,
  Radio,
  Receipt,
  Banknote,
  FileText,
  FlaskConical,
  Crosshair,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/fleet/status-badge";
import { CurriIcon } from "@/components/curri-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useDrivers } from "@/lib/hooks/use-drivers";
import { useVehicles } from "@/lib/hooks/use-vehicles";
import { useLoads } from "@/lib/hooks/use-loads";
import { useOrders } from "@/lib/hooks/use-orders";
import { useExceptions } from "@/lib/hooks/use-exceptions";
import type { LoadStatus, OrderStatus, ExceptionType } from "@/lib/api-types";
import { usePermissions } from "@/lib/hooks/use-permissions";
import type { Permission } from "@/lib/permissions";
import { STRATEGY_CATALOG } from "@/lib/strategies";
import { formatExpr } from "@/lib/workflows";
import { useWorkflows } from "@/lib/hooks/use-workflows";
import {
  useDefaultStrategy,
  useStrategies,
} from "@/lib/hooks/use-strategies";
import { cn } from "@/lib/utils";

interface NavItem {
  title: string;
  href: string;
  icon: LucideIcon;
  segment: string;
  badge?: number;
  requiredPermission?: Permission;
}

const typeIcons: Record<ExceptionType, typeof AlertTriangle> = {
  late_delivery: Clock,
  failed_stop: MapPinOff,
  off_route: AlertTriangle,
  driver_decline: UserX,
};

const typeLabels: Record<ExceptionType, string> = {
  late_delivery: "Late Delivery",
  failed_stop: "Failed Stop",
  off_route: "Off Route",
  driver_decline: "Driver Decline",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d`;
}

function getActiveSegment(pathname: string): string {
  if (pathname.startsWith("/fleet/drivers")) return "drivers";
  if (pathname.startsWith("/fleet/vehicles")) return "vehicles";
  if (pathname.startsWith("/orders")) return "orders";
  if (pathname.startsWith("/shipments")) return "shipments";
  if (pathname.startsWith("/loads")) return "loads";
  if (pathname.startsWith("/costs")) return "costs";
  if (pathname.startsWith("/strategies")) return "strategies";
  if (pathname.startsWith("/workflows")) return "workflows";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/payouts")) return "payouts";
  if (pathname.startsWith("/billing")) return "billing";
  if (pathname.startsWith("/ingestion")) return "ingestion";
  if (pathname.startsWith("/telematics")) return "telematics";
  if (pathname.startsWith("/settings")) return "settings";
  return "dashboard";
}

function ExceptionsPreview({ search }: { search: string }) {
  const { exceptions } = useExceptions();
  const [filter, setFilter] = React.useState<ExceptionType | "">("");

  const q = search.trim().toLowerCase();
  const filtered = exceptions.filter((e) => {
    if (filter && e.type !== filter) return false;
    if (!q) return true;
    return (
      e.description.toLowerCase().includes(q) ||
      e.type.toLowerCase().includes(q) ||
      e.load_id.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">
        <div className="flex items-center justify-between w-full">
          <span>{filtered.length} exception{filtered.length !== 1 ? "s" : ""}</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as ExceptionType | "")}
            className="text-xs bg-transparent border rounded px-1 py-0.5"
          >
            <option value="">All</option>
            <option value="late_delivery">Late</option>
            <option value="failed_stop">Failed</option>
            <option value="off_route">Off Route</option>
            <option value="driver_decline">Decline</option>
          </select>
        </div>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No exceptions</p>
        ) : (
          filtered.map((exc) => {
            const Icon = typeIcons[exc.type] ?? AlertTriangle;
            return (
              <Link
                key={exc.id}
                href={`/exceptions?id=${exc.id}`}
                className="flex flex-col gap-1 border-b px-4 py-3 text-sm leading-tight last:border-b-0 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Icon className="size-3 text-destructive shrink-0" />
                      <span className="text-xs font-medium truncate">
                        {typeLabels[exc.type] ?? exc.type}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                      {exc.description}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <span className="text-[11px] text-destructive font-medium">
                      {timeAgo(exc.created_at)}
                    </span>
                    {!exc.resolved_at && (
                      <Badge variant="destructive" className="text-[9px] px-1 py-0 h-4">
                        New
                      </Badge>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {exc.load_id.slice(0, 12)}...
                </span>
              </Link>
            );
          })
        )}
      </SidebarGroupContent>
    </>
  );
}

function DriversPreview({ search }: { search: string }) {
  const { drivers } = useDrivers(1, 50);
  const [filter, setFilter] = React.useState("");
  const q = search.trim().toLowerCase();
  const filtered = drivers.filter((d) => {
    if (filter && d.status !== filter) return false;
    if (!q) return true;
    return (
      d.name.toLowerCase().includes(q) ||
      (d.phone ?? "").toLowerCase().includes(q) ||
      (d.email ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">
        <div className="flex items-center justify-between w-full">
          <span>Drivers ({filtered.length})</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs bg-transparent border rounded px-1 py-0.5">
            <option value="">All</option>
            <option value="available">Available</option>
            <option value="en_route">En Route</option>
            <option value="assigned">Assigned</option>
            <option value="off_duty">Off Duty</option>
          </select>
        </div>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No drivers</p>
        ) : (
          filtered.map((d) => (
            <Link key={d.id} href={`/fleet/drivers?id=${d.id}`} className="flex flex-col gap-1 border-b px-4 py-3 text-sm leading-tight last:border-b-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
              <div className="flex items-center justify-between">
                <span className="font-medium text-xs">{d.name}</span>
                <StatusBadge status={d.status} />
              </div>
              <span className="text-[11px] text-muted-foreground">{d.phone} &middot; {d.carrier_type}</span>
            </Link>
          ))
        )}
      </SidebarGroupContent>
    </>
  );
}

function VehiclesPreview({ search }: { search: string }) {
  const { vehicles } = useVehicles(1, 50);
  const q = search.trim().toLowerCase();
  const filtered = !q
    ? vehicles
    : vehicles.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          (v.license_plate ?? "").toLowerCase().includes(q) ||
          (v.vin ?? "").toLowerCase().includes(q) ||
          v.class.toLowerCase().includes(q),
      );
  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">Vehicles ({filtered.length})</SidebarGroupLabel>
      <SidebarGroupContent>
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No vehicles</p>
        ) : (
          filtered.map((v) => (
            <Link key={v.id} href={`/fleet/vehicles?id=${v.id}`} className="flex flex-col gap-1 border-b px-4 py-3 text-sm leading-tight last:border-b-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
              <div className="flex items-center justify-between">
                <span className="font-medium text-xs">{v.name}</span>
                <StatusBadge status={v.is_active ? "active" : "inactive"} />
              </div>
              <span className="text-[11px] text-muted-foreground">{v.class} &middot; {v.license_plate}</span>
            </Link>
          ))
        )}
      </SidebarGroupContent>
    </>
  );
}

function LoadsPreview({ search }: { search: string }) {
  const [statusFilter, setStatusFilter] = React.useState<LoadStatus | "">("");
  const { loads } = useLoads(
    1,
    50,
    statusFilter ? { status: statusFilter as LoadStatus } : undefined,
  );
  const q = search.trim().toLowerCase();
  const filtered = !q
    ? loads
    : loads.filter(
        (l) =>
          l.id.toLowerCase().includes(q) ||
          (l.curri_delivery_id ?? "").toLowerCase().includes(q) ||
          l.delivery_method.toLowerCase().includes(q) ||
          l.status.toLowerCase().includes(q),
      );
  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">
        <div className="flex items-center justify-between w-full">
          <span>Loads ({filtered.length})</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LoadStatus | "")}
            className="text-xs bg-transparent border rounded px-1 py-0.5"
          >
            <option value="">All</option>
            <option value="planned">Planned</option>
            <option value="assigned">Assigned</option>
            <option value="in_transit">In Transit</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">
            No loads
          </p>
        ) : (
          filtered.map((l) => (
            <Link
              key={l.id}
              href={`/loads?id=${l.id}`}
              className="flex flex-col gap-1 border-b px-4 py-3 text-sm leading-tight last:border-b-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs">{l.id.slice(0, 8)}...</span>
                <StatusBadge status={l.status} />
              </div>
              <span className="text-[11px] text-muted-foreground">
                {l.delivery_method} &middot; {l.stops?.length ?? 0} stops
              </span>
            </Link>
          ))
        )}
      </SidebarGroupContent>
    </>
  );
}

function OrdersPreview({ search }: { search: string }) {
  const [statusFilter, setStatusFilter] = React.useState<OrderStatus | "">("");
  const { orders } = useOrders(
    1,
    50,
    statusFilter ? { status: statusFilter as OrderStatus } : undefined,
  );
  const q = search.trim().toLowerCase();
  const filtered = !q
    ? orders
    : orders.filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          (o.external_ref ?? "").toLowerCase().includes(q) ||
          (o.bol_number ?? "").toLowerCase().includes(q) ||
          o.status.toLowerCase().includes(q),
      );
  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">
        <div className="flex items-center justify-between w-full">
          <span>Orders ({filtered.length})</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as OrderStatus | "")}
            className="text-xs bg-transparent border rounded px-1 py-0.5"
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="accepted">Accepted</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">
            No orders
          </p>
        ) : (
          filtered.map((o) => (
            <Link
              key={o.id}
              href={`/orders?id=${o.id}`}
              className="flex flex-col gap-1 border-b px-4 py-3 text-sm leading-tight last:border-b-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs">
                  {o.external_ref ?? `${o.id.slice(0, 8)}...`}
                </span>
                <StatusBadge status={o.status} />
              </div>
              <span className="text-[11px] text-muted-foreground">
                {o.requested_method} &middot; {o.bol_number ?? "no BOL"}
              </span>
            </Link>
          ))
        )}
      </SidebarGroupContent>
    </>
  );
}

function BillingPreview() {
  // Lightweight preview pane for the Billing segment. The main
  // /billing page is the invoice list; this surface adds the
  // adjacent sub-pages (AR aging) plus the always-handy quick link
  // to all invoices. Per-customer surfaces (profile/charges/credits)
  // are reached via a customer detail page, not the sidebar.
  const pathname = usePathname();
  const items = [
    { href: "/billing", label: "Invoices" },
    { href: "/billing/ar-aging", label: "AR Aging" },
  ];
  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">Billing</SidebarGroupLabel>
      <SidebarGroupContent>
        {items.map((item) => {
          const isActive = pathname === item.href;
          return (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "flex items-center justify-between border-b px-4 py-3 text-sm leading-tight last:border-b-0 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
            >
              <span className="text-xs font-medium">{item.label}</span>
              {isActive && (
                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">
                  Open
                </Badge>
              )}
            </Link>
          );
        })}
      </SidebarGroupContent>
    </>
  );
}

function StrategiesPreview() {
  const searchParams = useSearchParams();
  const activeID = searchParams.get("id");
  const { strategies, isLoading } = useStrategies();
  const { defaultStrategy } = useDefaultStrategy();

  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">
        Strategies {isLoading ? "" : `(${strategies.length})`}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        {strategies.map((strategy) => {
          const meta = STRATEGY_CATALOG[strategy.mode];
          const Icon = meta.icon;
          const isSelected = activeID === strategy.id;
          const isDefault = defaultStrategy?.id === strategy.id;
          return (
            <Link
              key={strategy.id}
              href={`/strategies?id=${strategy.id}`}
              className={cn(
                "flex items-start gap-3 border-b px-4 py-3 text-sm leading-tight last:border-b-0 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                isSelected && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
            >
              <Icon className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium truncate">
                    {strategy.name}
                  </span>
                  {isDefault && (
                    <Badge
                      variant="secondary"
                      className="text-[9px] px-1.5 py-0 h-4 shrink-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900"
                    >
                      Default
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
                  {meta.label}
                </div>
              </div>
            </Link>
          );
        })}
        <Link
          href="/strategies"
          className="flex items-center gap-2 border-b px-4 py-2 text-xs text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          + New strategy
        </Link>
      </SidebarGroupContent>
    </>
  );
}

function WorkflowsPreview() {
  const searchParams = useSearchParams();
  const activeId = searchParams.get("id");
  const { workflows, isLoading } = useWorkflows();
  const { strategies } = useStrategies();
  const strategiesByID = new Map(strategies.map((s) => [s.id, s]));
  const sorted = [...workflows].sort((a, b) => a.priority - b.priority);

  return (
    <>
      <SidebarGroupLabel className="px-4 pt-2">
        Workflows {isLoading ? "" : `(${sorted.length})`}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        {sorted.map((wf, i) => {
          const isActive = activeId === wf.id;
          return (
            <Link
              key={wf.id}
              href={`/workflows?id=${wf.id}`}
              className={cn(
                "flex items-start gap-3 border-b px-4 py-3 text-sm leading-tight last:border-b-0 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
            >
              <Badge variant="secondary" className="font-mono w-6 justify-center shrink-0 mt-0.5">
                {i + 1}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium truncate">{wf.name}</span>
                  {!wf.is_enabled && (
                    <PowerOff className="size-3 text-muted-foreground shrink-0" />
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                  {formatExpr(wf.expr).pretty}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  → {strategiesByID.get(wf.strategy_id)?.name ?? "Unknown"}
                </div>
              </div>
            </Link>
          );
        })}
        <FallbackPreviewRow />
      </SidebarGroupContent>
    </>
  );
}

function FallbackPreviewRow() {
  const { defaultStrategy } = useDefaultStrategy();
  if (!defaultStrategy) return null;
  return (
    <div className="flex items-start gap-3 border-b px-4 py-3 text-sm leading-tight bg-muted/30">
      <GitBranch className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-muted-foreground">
          Fallback
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          No workflows matched
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          → {defaultStrategy.name}
        </div>
      </div>
    </div>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const pathname = usePathname();
  const { setOpen } = useSidebar();
  const activeSegment = getActiveSegment(pathname);
  const { exceptions } = useExceptions();
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    setSearch("");
  }, [activeSegment]);

  const { can } = usePermissions();

  const searchPlaceholder =
    activeSegment === "drivers"
      ? "Search drivers on this page..."
      : activeSegment === "vehicles"
      ? "Search vehicles on this page..."
      : activeSegment === "orders"
      ? "Search orders on this page..."
      : activeSegment === "loads"
      ? "Search loads on this page..."
      : activeSegment === "dashboard"
      ? "Search exceptions on this page..."
      : "Search...";

  // Exceptions removed from top nav — the Dashboard already surfaces them
  // prominently, and they remain reachable via the dashboard "Exceptions"
  // card. Keeping them here was redundant nav.
  const allNavItems: NavItem[] = [
    { title: "Dashboard", href: "/dashboard", icon: LayoutDashboard, segment: "dashboard", badge: exceptions.length },
    { title: "Orders", href: "/orders", icon: Package, segment: "orders", requiredPermission: "org:loads:read" },
    { title: "Loads", href: "/loads", icon: Truck, segment: "loads", requiredPermission: "org:loads:read" },
    { title: "Ingestion", href: "/ingestion", icon: Inbox, segment: "ingestion" },
    { title: "Telematics", href: "/telematics", icon: Radio, segment: "telematics" },
    { title: "Drivers", href: "/fleet/drivers", icon: Users, segment: "drivers", requiredPermission: "org:drivers:read" },
    { title: "Vehicles", href: "/fleet/vehicles", icon: Truck, segment: "vehicles", requiredPermission: "org:vehicles:read" },
    { title: "Costs", href: "/costs", icon: DollarSign, segment: "costs", requiredPermission: "org:costs:read" },
    { title: "Strategies", href: "/strategies", icon: Workflow, segment: "strategies" },
    { title: "Workflows", href: "/workflows", icon: GitBranch, segment: "workflows" },
    { title: "Brokers", href: "/brokers", icon: Building2, segment: "brokers" },
    { title: "Pricing", href: "/pricing", icon: Receipt, segment: "pricing", requiredPermission: "org:pricing:read" },
    { title: "Payouts", href: "/payouts", icon: Banknote, segment: "payouts", requiredPermission: "org:payouts:read" },
    { title: "Billing", href: "/billing", icon: FileText, segment: "billing", requiredPermission: "org:billing:read" },
  ];

  const navItems = allNavItems.filter((item) => !item.requiredPermission || can(item.requiredPermission));

  const activeNav = navItems.find((i) => i.segment === activeSegment);

  return (
    <Sidebar
      collapsible="icon"
      className="overflow-hidden *:data-[sidebar=sidebar]:flex-row"
      {...props}
    >
      <Sidebar
        collapsible="none"
        className="w-[calc(var(--sidebar-width-icon)+1px)]! border-r"
      >
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" className="md:h-8 md:p-0" render={<Link href="/dashboard" />}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <CurriIcon className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Curri</span>
                  <span className="truncate text-xs">DOP</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent className="px-1.5 md:px-0">
              <SidebarMenu>
                {navItems.map((item) => {
                  const isActive = item.segment === activeSegment;
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        tooltip={{ children: item.title, hidden: false }}
                        onClick={() => setOpen(true)}
                        isActive={isActive}
                        className="px-2.5 md:px-2 relative"
                        render={<Link href={item.href} />}
                      >
                        <item.icon />
                        <span>{item.title}</span>
                        {item.badge != null && item.badge > 0 && (
                          <Badge variant="destructive" className="absolute -top-1 -right-1 text-[9px] px-1 py-0 h-4 min-w-4 flex items-center justify-center">
                            {item.badge}
                          </Badge>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex flex-col items-center gap-2 py-2">
                <ThemeToggle />
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: "h-7 w-7",
                      // The default popover has no height cap, so on
                      // shorter viewports the bottom items (Simulation,
                      // API keys, Regions) clip below the fold and the
                      // "Sign out" footer hides them entirely. Capping
                      // the action list and letting it scroll keeps
                      // every custom link reachable. 60vh leaves room
                      // for the user header + sign-out footer.
                      userButtonPopoverActions: {
                        maxHeight: "60vh",
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  <UserButton.MenuItems>
                    <UserButton.Link
                      label="Organization settings"
                      labelIcon={<Settings className="size-4" />}
                      href="/settings"
                    />
                    <UserButton.Link
                      label="Team"
                      labelIcon={<Users className="size-4" />}
                      href="/settings/team"
                    />
                    <UserButton.Link
                      label="Simulation"
                      labelIcon={<FlaskConical className="size-4" />}
                      href="/settings/simulation"
                    />
                    <UserButton.Link
                      label="Fulfillment"
                      labelIcon={<Crosshair className="size-4" />}
                      href="/settings/fulfillment"
                    />
                    <UserButton.Link
                      label="Cost model"
                      labelIcon={<DollarSign className="size-4" />}
                      href="/costs"
                    />
                    <UserButton.Link
                      label="Branches"
                      labelIcon={<Settings className="size-4" />}
                      href="/settings/branches"
                    />
                    <UserButton.Link
                      label="Regions"
                      labelIcon={<Settings className="size-4" />}
                      href="/settings/regions"
                    />
                    <UserButton.Link
                      label="API keys"
                      labelIcon={<KeyRound className="size-4" />}
                      href="/settings/api-keys"
                    />
                  </UserButton.MenuItems>
                </UserButton>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <Sidebar collapsible="none" className="hidden flex-1 md:flex">
        <SidebarHeader className="gap-3.5 border-b p-4">
          <div className="flex w-full items-center justify-between">
            <div className="text-base font-medium text-foreground">
              {activeNav?.title ?? "Exceptions"}
            </div>
            <OrgSwitcher />
          </div>
          <SidebarInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={activeSegment === "costs" || activeSegment === "settings"}
          />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup className="px-0">
            {activeSegment === "dashboard" && <ExceptionsPreview search={search} />}
            {activeSegment === "drivers" && <DriversPreview search={search} />}
            {activeSegment === "vehicles" && <VehiclesPreview search={search} />}
            {activeSegment === "orders" && <OrdersPreview search={search} />}
            {activeSegment === "loads" && <LoadsPreview search={search} />}
            {activeSegment === "strategies" && <StrategiesPreview />}
            {activeSegment === "workflows" && <WorkflowsPreview />}
            {activeSegment === "billing" && <BillingPreview />}
            {(activeSegment === "costs" || activeSegment === "settings") && (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                Select a section to preview
              </div>
            )}
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    </Sidebar>
  );
}
